import Vue from 'vue'
// import axios from 'axios'
import { getFirestore, collection, getDocs, query, where, orderBy, startAt, endAt, limit, startAfter } from 'firebase/firestore'
import { tracking_candidates } from '@/utils/barcode'

const initial_state = () => ({ 
    parcels: {},
    ds_last_fetch: {},
    order_prefixes: {},
    selected_data_source_id: '',
})

const state = initial_state()

const actions = {
  async search_parcels({ commit, state }, { query: searchQuery, data_source_id }) {
    const normalizedQuery = (searchQuery || '').toString().trim()
    if (!normalizedQuery) {
      // If query is empty, reset the state and fetch regular data
      commit('RESET_STATE');
      return await this.dispatch('fulfillments/fetch_parcels', { rows: 10 });
    }
    
    // Use the data source ID from the parameters or fall back to the state
    const dsId = data_source_id || state.selected_data_source_id;
    if (!dsId) return [];
    
    try {
      // Get a reference to the fulfillments collection for the selected data source
      const db = getFirestore();
      const fulfillmentsRef = collection(db, 'data_sources', dsId, 'fulfillments');
      
      // Create an array to hold all our query promises
      const queryPromises = [];
      
      // Search for fulfillment number (name field)
      const query_for_name = (q) => {
        q = (q || '').toString()
        const prefix = state.order_prefixes[dsId] || '#'
        return q[0] === prefix ? q : `${prefix}${q}`
      }

      queryPromises.push(
        getDocs(
          query(
            fulfillmentsRef,
            orderBy('name'),
            startAt(query_for_name(normalizedQuery)),
            endAt(query_for_name(normalizedQuery) + '\uf8ff'),
            limit(50)
          )
        )
      );
      
      // Search for customer name
      // Use orderBy + startAt/endAt for prefix search
      queryPromises.push(
        getDocs(
          query(
            fulfillmentsRef,
            orderBy('shipping_address.name'),
            startAt(normalizedQuery),
            endAt(normalizedQuery + '\uf8ff'),
            limit(50)
          )
        )
      );
      
      // Search for tracking number: prefix on what was typed, plus exact matches on every plausible
      // tracking number inside a scanned label barcode (FedEx 34-digit codes, GS1 codes, spaced numbers)
      const candidates = tracking_candidates(normalizedQuery)
      const compact = candidates[0] || normalizedQuery.toUpperCase()
      queryPromises.push(
        getDocs(
          query(
            fulfillmentsRef,
            orderBy('tracking_number'),
            startAt(compact),
            endAt(compact + '\uf8ff'),
            limit(50)
          )
        )
      );
      if(candidates.length) queryPromises.push(
        getDocs(query(fulfillmentsRef, where('tracking_numbers', 'array-contains-any', candidates), limit(50)))
      );
      
      // Execute all queries in parallel
      const querySnapshots = await Promise.all(queryPromises);
      
      // Combine and deduplicate results
      const searchResults = [];
      const seenIds = new Set();
      
      querySnapshots.forEach(snapshot => {
        snapshot.forEach(doc => {
          if (!seenIds.has(doc.id)) {
            seenIds.add(doc.id);
            searchResults.push({
              ...doc.data(),
              sf_fulfillment_id: doc.id
            });
          }
        });
      });
      
      // Sort by created_at in descending order (newest first)
      searchResults.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      // Update the state with search results
      commit('SET_PARCELS', { parcels: searchResults, data_source_id: dsId });
      
      // If no results found, log a message to the console
      if (searchResults.length === 0) {
      console.log(`No fulfillments found matching "${normalizedQuery}"`);
      } else {
        console.log(`Found ${searchResults.length} fulfillments matching "${normalizedQuery}"`);
      }
      
      return searchResults;
    } catch (error) {
      console.error('Error searching parcels:', error);
      throw error;
    }
  },
  
  // Deletes the label of a fulfilled parcel at the carrier and drops its refs from the fulfillment
  async delete_label({ commit, dispatch, state }, { fulfillment, tracking_number }){
    const data_source_id = fulfillment.sf_ds_id || fulfillment.sf_data_source_id || state.selected_data_source_id
    const label_refs = (fulfillment.sf_label_refs || []).map(l => l.ref)
    const deleted = await dispatch('warehouse/delete_label', {
      carrier_id: fulfillment.sf_carrier_account_id,
      tracking_number,
      label_refs,
      data_source_id,
      fulfillment_id: fulfillment.sf_fulfillment_id,
    }, { root: true })
    if(!deleted) return false
    commit('UPDATE_PARCEL', { sf_fulfillment_id: fulfillment.sf_fulfillment_id, data_source_id, data: { sf_label_refs: [] } })
    return true
  },

  async fetch_parcels({ commit, state }, { rows }){
    const rowsToFetch = rows - (state.parcels[state.selected_data_source_id] || []).length
    console.log('rowsToFetch', rowsToFetch);
    if(rowsToFetch < 1) return
    const q = query(
      collection(getFirestore(), 'data_sources', state.selected_data_source_id, 'fulfillments'),
      orderBy('updated_at', 'desc'),
      startAfter(state.ds_last_fetch[state.selected_data_source_id] || new Date('2999-01-01').toISOString()),
      limit(rowsToFetch)
    )
    const ds_parcels = await getDocs(q).then(async res => {
      const docs = await Promise.all(res.docs.map(d => ({
        ...d.data(),
        sf_fulfillment_id: d.id
      })))
      return { parcels: docs, last_fetch: { date: docs.length && docs[docs.length - 1].updated_at }}
    })
              console.log('ds_parcels', ds_parcels)
      const parcels = (state.parcels[state.selected_data_source_id] || [])
        .concat(ds_parcels.parcels)
      parcels.sort((a, b) => b.created_at - a.created_at) // TODO
      const last_fetches = { [state.selected_data_source_id]: ds_parcels.last_fetch.date }
      // ds_parcels.forEach(ds => {
      //   if(!ds.last_fetch.date) return
      //   last_fetches[ds.last_fetch.ds_id] = ds.last_fetch.date
      // })
      commit('SET_STATE', { ds_last_fetch: {
          ...state.ds_last_fetch,
          ...last_fetches,
          },
          order_prefixes: {
            ...state.order_prefixes,
            ...(parcels?.[0]?.name?.[0] ? 
              { 
                [state.selected_data_source_id]: parcels?.[0]?.name?.[0]
              } : {}
            )
          }
        })
      console.log('fetch_parcels', parcels.map(p => p.name))
      commit('SET_PARCELS', { parcels })
  }
}

const mutations = {
      SET_PARCELS(state, { parcels, data_source_id } ){
        const targetId = data_source_id || state.selected_data_source_id
        Vue.set(state.parcels, targetId, parcels)
      },
      SET_STATE(state, payload){
        for(const key in payload){
          Vue.set(state, key, payload[key])
        }
      },
      RESET_STATE(state){
        const currentDataSourceId = state.selected_data_source_id;
        const initialState = initial_state();
        
        // Reset everything except selected_data_source_id
        for(const key in initialState){
          if (key !== 'selected_data_source_id' && key !== 'order_prefixes') {
            Vue.set(state, key, initialState[key]);
          }
        }
        
        // Preserve the selected_data_source_id
        state.selected_data_source_id = currentDataSourceId;
      },
      UPDATE_PARCEL(state, { sf_fulfillment_id, data_source_id, data }){
        const targetId = data_source_id || state.selected_data_source_id
        const parcels = state.parcels[targetId] || []
        Vue.set(state.parcels, targetId, parcels.map(p => p.sf_fulfillment_id === sf_fulfillment_id ? { ...p, ...data } : p))
      },
      UNSHIFT_PARCEL(state, { sf_ds_id, ...payload }){
        const targetId = sf_ds_id || state.selected_data_source_id
        const existing = state.parcels[targetId] || []
        Vue.set(state.parcels, targetId, [payload, ...existing])
      }
}

const getters = {
  get_server_fulfillments: (state, getters, rootState) => {
    // return rootState.warehouse.company.data_sources.reduce((tot, ds) => tot += ds.fulfillments || 0, 0)
    return rootState.warehouse.company.data_sources.find(ds => ds.id === state.selected_data_source_id)?.fulfillments || 0
  },
  get_active_fulfillments: (state) => {
    return state.parcels[state.selected_data_source_id] || []
  }
}

export default {
    state,
    getters,
    actions, 
    mutations,
    namespaced: true,
}
